
import { useState } from "react";
import { NavigateFunction } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { storeFollowedStrategy } from "@/utils/strategyUtils";
import { toast } from "sonner";
import FreeStrategyFeatures from "./FreeStrategyFeatures";

interface FreeStrategyActionsProps {
  strategy: any;
  isSubmitting: boolean;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  navigate: NavigateFunction;
  originalStrategyId?: string;
}

const FreeStrategyActions = ({ 
  strategy, 
  isSubmitting, 
  setIsSubmitting, 
  navigate, 
  originalStrategyId 
}: FreeStrategyActionsProps) => {
  const { toast: uiToast } = useToast();
  
  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Ensure we have a valid strategy object with the correct ID
        const strategyToFollow = {
          ...strategy,
          id: strategy.id || originalStrategyId
        };
        
        console.log("Following strategy with data:", strategyToFollow);
        
        const success = await storeFollowedStrategy({
          strategy: strategyToFollow,
          userId: user.id,
          originalStrategyId: originalStrategyId || strategyToFollow.id || ""
        });
        
        if (!success) {
          throw new Error("Failed to follow strategy");
        }
      } else {
        console.log("User not authenticated, strategy won't be saved to account");
        toast("Authentication required", {
          description: "Please sign in to follow strategies"
        });
        navigate('/auth');
        return;
      }
      
      // Add a short delay before navigation for toast to be visible
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Make sure we're navigating to thank-you-free
      navigate('/thank-you-free', {
        state: {
          strategy: strategy
        }
      });
    } catch (error) {
      console.error("Error following strategy:", error);
      uiToast({
        title: "Error",
        description: "Failed to follow strategy. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6">Free Strategies</h2>
      <FreeStrategyFeatures />
      
      <Button 
        onClick={handleConfirm} 
        disabled={isSubmitting} 
        className="w-full bg-[#1B2F47] hover:bg-[#2C4A6F] text-white py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all"
      >
        {isSubmitting ? "Processing..." : "Confirm and Start Trading"}
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};

export default FreeStrategyActions;
